import React from 'react'
import { InflatedInterfaceBomItem, InflatedProductBom } from './bom/bomUtils'
import classNames from 'classnames'
import SquareContainer from './components/SquareContainer'
import ConfigurableProductView from './components/ConfigurableProductView'
import { getDefaultOrientation } from './products/productUtils'
import { getSkinErrorImage, getSkinPlaceholderImage } from './data/test-data'
import ConfigurableProductButton from './components/ConfigurableProductButton'
import { Cross } from './characters'
import { getListInterfaceDisplayName } from './list/listItemUtils'
import { LedModule } from './data/LedModule'
import './FinishedProductComponentBreakdown.scss'
import { SmallPill } from './components/SmallPill'


function aggregateInterfaces(items: readonly InflatedInterfaceBomItem[]) {
  return items.reduce<{ item: InflatedInterfaceBomItem, count: number }[]>((accum, next) => {
    const existing = accum.find(other =>
      other.item.interfaceItemNo === next.interfaceItemNo
      && other.item.mechItemNo === next.mechItemNo
    )

    if (existing) {
      existing.count += 1
      return accum
    }

    return [...accum, {
      item: next,
      count: 1,
    }]
  }, [])
}


export const FinishedProductComponentBreakdown: React.FC<{ bom: InflatedProductBom }> = (props) => {
  const { skinProduct, gridProduct, interfaces, orientation } = props.bom

  const aggregatedInterfaces = aggregateInterfaces(interfaces)

  return (
    <div className="FinishedProductComponentBreakdown">
      <div className="productPreviewAndHeaderContainer">
        <div className={classNames('previewContainer', { horizontal: orientation === 'Horizontal' })}>
          <SquareContainer size="100%">
            <ConfigurableProductView
              shadowScale={0.2}
              {...skinProduct}
              gridProduct={gridProduct}
              orientation={orientation}
              buttonCoordsOrientation={getDefaultOrientation(gridProduct)}
              image={skinProduct.image}
              placeholderImage={getSkinPlaceholderImage(skinProduct, gridProduct)}
              errorImage={getSkinErrorImage(skinProduct, gridProduct)}
              renderButton={(buttonConfig, buttonIndex, scale, shadowScale) => {
                const interfaceProduct = interfaces[buttonIndex].interfaceProduct
                return (
                  <ConfigurableProductButton
                    shadowScale={shadowScale}
                    baseScale={scale}
                    {...buttonConfig}
                    // backgroundImage={buttons[buttonIndex].mechImageUrl}
                    foregroundImage={interfaceProduct.image}
                    debugColorCode={interfaceProduct.color}
                  />
                )
              }}
            />
          </SquareContainer>
        </div>
        <div className="productHeader">
          {
            (gridProduct && !!gridProduct?.includedSkinItemNo) ? (
              <>
                <div className="description">{gridProduct.description}</div>
                <div className="itemNo">{gridProduct.itemNo} (Composite Product - includes grid{
                  skinProduct.itemNo === gridProduct?.includedSkinItemNo && ' and skin'
                })
                </div>
                {
                  skinProduct.itemNo !== gridProduct?.includedSkinItemNo && (
                    <div className="itemNo">{skinProduct.itemNo} (Skin{gridProduct?.includedSkinItemNo && ', replace included skin'})</div>
                  )
                }
              </>
            ) : (
              <>
                <div className="description">{skinProduct.description}</div>
                <div className="itemNo">{skinProduct.itemNo} (Skin)</div>
                <div className="itemNo">{gridProduct?.itemNo} (Grid)</div>
              </>
            )
          }
        </div>
      </div>

      {aggregatedInterfaces.length > 0 && (
        <div className="mechanisms">
          <h4>Mechanisms</h4>
          {aggregatedInterfaces.map((item, index) => {
            const buttonOption = item.item
            const { interfaceProduct, mechProduct, parentInterfaceProduct } = buttonOption

            const interfaceItemNo = parentInterfaceProduct?.itemNo ?? interfaceProduct.itemNo
            // if no description on child item
            const interfaceDescription = (interfaceProduct.description || parentInterfaceProduct?.description) ?? ''

            return (
              <div key={index}>
                <div className="quantityDescriptor">
                  {item.count > 1 && `${item.count} ${Cross}`} {getListInterfaceDisplayName(item.item.interfaceProduct, item.item.mechProduct)}
                </div>
                <ul>
                  <li>
                    <strong>{interfaceItemNo}</strong>
                    {buttonOption.interfaceIncludedWithSkin && (<SmallPill>Included with skin</SmallPill>)}
                    {!buttonOption.interfaceIncludedWithMech && !!parentInterfaceProduct && (<SmallPill>Included in pack</SmallPill>)}
                    {!buttonOption.interfaceIncludedWithSkin && buttonOption.interfaceIncludedWithMech && (
                      <SmallPill>Included with mechanism</SmallPill>)}
                    <div className={'secondaryDesc'}>{interfaceDescription}</div>
                  </li>
                  {mechProduct && (
                    <li>
                      <strong>{mechProduct.itemNo}</strong>
                      {buttonOption.mechIncludedWithGrid && (<SmallPill>Included with grid</SmallPill>)}
                      <div className={'secondaryDesc'}>{mechProduct.description}</div>
                    </li>
                  )}
                  {buttonOption.addLedEnabled && (<li><strong>{LedModule.partNumber}</strong> {LedModule.name}</li>)}
                </ul>
              </div>
            )
          })}
        </div>
      )}

    </div>
  )
}

export default FinishedProductComponentBreakdown
